import { useEffect, useState } from 'react';
import { Download, Loader2, Mail, Send, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export function WaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [invitingId, setInvitingId] = useState(null);

  const handleInvite = async (entry) => {
    setInvitingId(entry.id);
    try {
      const res = await api.inviteWaitlistArtist(entry.id);
      setEntries((prev) => prev.map((e) =>
        e.id === entry.id ? { ...e, invited: true, invited_at: res.data.invited_at } : e
      ));
      toast.success(`Setup email sent to ${entry.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInvitingId(null);
    }
  };

  useEffect(() => {
    api.getWaitlist()
      .then((res) => setEntries(res.data || []))
      .catch(() => toast.error('Failed to load waitlist'))
      .finally(() => setLoading(false));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.exportWaitlist();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.setAttribute('download', `tune4tune-waitlist-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const artistCount = entries.filter((e) => e.role === 'artist').length;
  const fanCount = entries.filter((e) => e.role === 'fan').length;

  return (
    <div className="bg-surface border border-[#262626] rounded-xl p-6" data-testid="waitlist-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading text-xl font-bold mb-1 flex items-center gap-2">
            <Mail className="w-5 h-5 text-lime" />Waitlist
          </h3>
          <p className="text-sm text-[#A1A1AA]">
            {entries.length} signups · {artistCount} artists · {fanCount} fans
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || entries.length === 0}
          className="btn-primary"
          data-testid="export-waitlist-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Export Excel
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#A1A1AA]" /></div>
      ) : entries.length === 0 ? (
        <p className="text-center text-[#A1A1AA] py-10">No waitlist signups yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626]">
                <th className="text-left p-4 text-sm text-[#A1A1AA]">Email</th>
                <th className="text-left p-4 text-sm text-[#A1A1AA]">Role</th>
                <th className="text-left p-4 text-sm text-[#A1A1AA]">Artist Name</th>
                <th className="text-left p-4 text-sm text-[#A1A1AA]">Signed Up</th>
                <th className="text-left p-4 text-sm text-[#A1A1AA]">Invite</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-[#262626] hover:bg-surface-highlight">
                  <td className="p-4 font-medium">{e.email}</td>
                  <td className="p-4">
                    <Badge className={`capitalize ${e.role === 'artist' ? 'bg-lime/20 text-lime' : 'bg-blue-500/20 text-blue-400'}`}>
                      {e.role}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm" data-testid={`waitlist-artist-name-${e.id}`}>
                    {e.artist_name || <span className="text-[#555]">—</span>}
                  </td>
                  <td className="p-4 text-sm text-[#A1A1AA]">
                    {e.created_at ? new Date(e.created_at).toLocaleString() : '-'}
                  </td>
                  <td className="p-4">
                    {e.role === 'artist' ? (
                      e.invited ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-500/20 text-green-400" data-testid={`waitlist-invited-badge-${e.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Invited
                          </Badge>
                          <button
                            onClick={() => handleInvite(e)}
                            disabled={invitingId === e.id}
                            className="text-xs text-[#A1A1AA] hover:text-lime underline underline-offset-2 disabled:opacity-50"
                            data-testid={`waitlist-resend-invite-btn-${e.id}`}
                          >
                            {invitingId === e.id ? 'Sending…' : 'Resend'}
                          </button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleInvite(e)}
                          disabled={invitingId === e.id}
                          className="btn-primary h-8 text-xs"
                          data-testid={`waitlist-invite-btn-${e.id}`}
                        >
                          {invitingId === e.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <><Send className="w-3 h-3 mr-1.5" />Invite as Artist</>}
                        </Button>
                      )
                    ) : (
                      <span className="text-[#555] text-sm">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
